import { useQuery, useMutation } from "convex/react";
import { useNavigate } from "react-router-dom";
import { api } from "convex/_generated/api";
import { useUser } from "../hooks/useUser";
import type { Id } from "convex/_generated/dataModel";

export function PuzzleSelectPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const puzzles = useQuery(api.puzzles.list, {});
  const createSession = useMutation(api.sessions.create);

  const handleSelectPuzzle = async (puzzleId: Id<"puzzles">) => {
    if (!user) return;

    try {
      const { shareCode } = await createSession({
        puzzleId,
        userId: user.id,
      });
      navigate(`/play/${shareCode}`);
    } catch (error) {
      console.error("Failed to create session:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Select a Puzzle</h1>

        {puzzles === undefined ? (
          <div className="text-center py-12 text-gray-500">
            Loading puzzles...
          </div>
        ) : puzzles.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No puzzles available yet.</p>
            <p className="text-sm text-gray-400">
              Visit the <a href="/admin" className="text-blue-600 hover:underline">admin page</a> to seed sample puzzles.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {puzzles.map((puzzle) => (
              <button
                key={puzzle._id}
                onClick={() => handleSelectPuzzle(puzzle._id)}
                className="bg-white rounded-lg shadow p-6 text-left hover:shadow-md transition-shadow"
              >
                <h3 className="font-semibold text-lg mb-1">{puzzle.title}</h3>
                {puzzle.author && (
                  <p className="text-sm text-gray-600 mb-2">
                    by {puzzle.author}
                  </p>
                )}
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>
                    {puzzle.dimensions.cols}×{puzzle.dimensions.rows}
                  </span>
                  {puzzle.difficulty && <span>{puzzle.difficulty}</span>}
                  <span>{new Date(puzzle.date).toLocaleDateString()}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
